import { useEffect, useState, type FormEvent } from "react";
import QRCode from "qrcode";
import { api } from "../../lib/api";
import { useToast } from "../shell/Toast";
import { useConfirm } from "../shell/ConfirmDialog";

export function TwoFactorSettings() {
  const toast = useToast();
  const { confirm } = useConfirm();
  const [enabled, setEnabled] = useState(false);
  const [backupLeft, setBackupLeft] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [setup, setSetup] = useState<{
    challengeToken: string;
    secret: string;
    otpauthUrl: string;
    qrDataUrl: string;
  } | null>(null);
  const [setupCode, setSetupCode] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [disablePassword, setDisablePassword] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [regenPassword, setRegenPassword] = useState("");
  const [regenCode, setRegenCode] = useState("");

  async function refresh() {
    const s = await api.twoFaStatus();
    setEnabled(s.enabled);
    setBackupLeft(s.backupCodesRemaining);
  }

  useEffect(() => {
    void refresh().catch(() => undefined);
  }, []);

  async function startSetup() {
    setBusy(true);
    setError("");
    setBackupCodes(null);
    try {
      const res = await api.twoFaSetup();
      const qrDataUrl = await QRCode.toDataURL(res.otpauthUrl, {
        width: 220,
        margin: 2,
        errorCorrectionLevel: "M",
      });
      setSetup({
        challengeToken: res.challengeToken,
        secret: res.secret,
        otpauthUrl: res.otpauthUrl,
        qrDataUrl,
      });
      setSetupCode("");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function confirmSetup(e: FormEvent) {
    e.preventDefault();
    if (!setup) return;
    setBusy(true);
    setError("");
    try {
      const res = await api.twoFaEnable({
        challengeToken: setup.challengeToken,
        code: setupCode.trim(),
      });
      setBackupCodes(res.backupCodes);
      setSetup(null);
      await refresh();
      toast.push({ message: "Autenticazione a due fattori attiva" });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function disable2fa(e: FormEvent) {
    e.preventDefault();
    if (
      !(await confirm({
        title: "Disattiva 2FA",
        message: "Rimuovere l’Authenticator da questo account?",
        confirmLabel: "Disattiva",
        tone: "danger",
      }))
    ) {
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api.twoFaDisable({
        password: disablePassword,
        code: disableCode.trim(),
      });
      setDisablePassword("");
      setDisableCode("");
      setBackupCodes(null);
      await refresh();
      toast.push({ message: "2FA disattivato" });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function regenCodes(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await api.twoFaRecoveryCodes({
        password: regenPassword,
        code: regenCode.trim(),
      });
      setBackupCodes(res.backupCodes);
      setRegenPassword("");
      setRegenCode("");
      await refresh();
      toast.push({ message: "Nuovi codici di recupero generati" });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="settings-blocks">
      <section className="settings-block">
        <h3>Autenticazione a due fattori (Authenticator)</h3>
        <p className="help">
          Usa Google Authenticator, Microsoft Authenticator, Authy o qualsiasi app TOTP. Dopo la
          password ti verrà chiesto un codice a 6 cifre.
        </p>
        <div className="pref-row">
          <div>
            <strong>Stato 2FA</strong>
            <span>
              {enabled
                ? `Attivo · ${backupLeft} codici di recupero rimasti`
                : "Non attivo — consigliato per accessi più sicuri"}
            </span>
          </div>
          <span className={`totp-badge ${enabled ? "on" : "off"}`}>{enabled ? "ON" : "OFF"}</span>
        </div>
        {error ? <div className="error">{error}</div> : null}
      </section>

      {!enabled && !setup ? (
        <section className="settings-block">
          <h3>Collega Authenticator</h3>
          <p className="help">
            Scansiona un QR con l’app Authenticator, oppure inserisci la chiave manualmente.
          </p>
          <button className="btn btn-primary" type="button" disabled={busy} onClick={() => void startSetup()}>
            {busy ? "Preparazione…" : "Avvia connessione 2FA"}
          </button>
        </section>
      ) : null}

      {setup ? (
        <section className="settings-block">
          <h3>Configura l’app</h3>
          <div className="totp-setup">
            <img className="totp-qr" src={setup.qrDataUrl} alt="QR code Authenticator" width={220} height={220} />
            <div className="totp-setup-meta">
              <p className="help">Se non puoi scansionare, inserisci questa chiave in Authenticator:</p>
              <code className="totp-secret">{setup.secret}</code>
              <button
                className="btn"
                type="button"
                onClick={() => {
                  void navigator.clipboard.writeText(setup.secret);
                  toast.push({ message: "Chiave copiata" });
                }}
              >
                Copia chiave
              </button>
            </div>
          </div>
          <form className="form totp-confirm-form" onSubmit={(e) => void confirmSetup(e)}>
            <label className="label" htmlFor="totp-setup-code">
              Codice a 6 cifre dall’app
            </label>
            <input
              id="totp-setup-code"
              className="input"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="123456"
              value={setupCode}
              onChange={(e) => setSetupCode(e.target.value)}
              required
            />
            <div className="actions">
              <button className="btn btn-primary" type="submit" disabled={busy}>
                Conferma e attiva
              </button>
              <button
                className="btn"
                type="button"
                disabled={busy}
                onClick={() => {
                  setSetup(null);
                  setSetupCode("");
                }}
              >
                Annulla
              </button>
            </div>
          </form>
        </section>
      ) : null}

      {backupCodes ? (
        <section className="settings-block totp-backup-block">
          <h3>Codici di recupero</h3>
          <p className="help">
            Salvali in un posto sicuro. Ogni codice funziona una sola volta se perdi il telefono.
          </p>
          <ul className="totp-backup-list">
            {backupCodes.map((code) => (
              <li key={code}>
                <code>{code}</code>
              </li>
            ))}
          </ul>
          <button
            className="btn"
            type="button"
            onClick={() => {
              void navigator.clipboard.writeText(backupCodes.join("\n"));
              toast.push({ message: "Codici copiati" });
            }}
          >
            Copia tutti
          </button>
        </section>
      ) : null}

      {enabled ? (
        <>
          <section className="settings-block">
            <h3>Rigenera codici di recupero</h3>
            <form className="form" onSubmit={(e) => void regenCodes(e)}>
              <div className="totp-inline-fields">
                <div>
                  <label className="label" htmlFor="regen-pass">
                    Password
                  </label>
                  <input
                    id="regen-pass"
                    className="input"
                    type="password"
                    autoComplete="current-password"
                    value={regenPassword}
                    onChange={(e) => setRegenPassword(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="label" htmlFor="regen-code">
                    Codice Authenticator
                  </label>
                  <input
                    id="regen-code"
                    className="input"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={regenCode}
                    onChange={(e) => setRegenCode(e.target.value)}
                    required
                  />
                </div>
              </div>
              <button className="btn" type="submit" disabled={busy}>
                Genera nuovi codici
              </button>
            </form>
          </section>

          <section className="settings-block">
            <h3>Disattiva 2FA</h3>
            <form className="form" onSubmit={(e) => void disable2fa(e)}>
              <div className="totp-inline-fields">
                <div>
                  <label className="label" htmlFor="dis-pass">
                    Password
                  </label>
                  <input
                    id="dis-pass"
                    className="input"
                    type="password"
                    autoComplete="current-password"
                    value={disablePassword}
                    onChange={(e) => setDisablePassword(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="label" htmlFor="dis-code">
                    Codice Authenticator o recupero
                  </label>
                  <input
                    id="dis-code"
                    className="input"
                    value={disableCode}
                    onChange={(e) => setDisableCode(e.target.value)}
                    required
                  />
                </div>
              </div>
              <button className="btn btn-danger" type="submit" disabled={busy}>
                Disattiva 2FA
              </button>
            </form>
          </section>
        </>
      ) : null}
    </div>
  );
}
